'use client';

import clsx from 'clsx';
import Text from '@/components/Text/Text';
import styles from './Plans.module.scss';
import Button, { ButtonType } from '@/components/Button/Button';
import { mediaPath } from '@/mediaPath';

type PlansSectionData = {
  id: number;
  title: string;
  description?: string;
  cards: {
    id: string;
    button: {
      id: number;
      link: string;
      title: string;
      type: ButtonType;
      iconName?: string;
      target: '_self' | '_blank' | string;
    };
    title: string;
    price: string;
    description: string;
    duration?: string;
    badge?: string | null;
  }[];
  background: {
    url: string;
    alt: string;
  } | null;
};

const PlansSection = ({ data }: { data: PlansSectionData }) => {
  const { cards } = data;
  const hasAnyBadge = cards?.some((card) => card.badge) ?? false;

  return (
    <section className={styles.plansSection}>
      <div className={styles.topContainer}>
        <Text tag='h2'>{data.title}</Text>
        <p className={styles.plansDescription}>{data.description}</p>
      </div>

      <div
        className={clsx(styles.plansContainer, {
          [styles.withoutBackground]: !data.background?.url,
        })}
        style={data.background?.url ? {
          background: `linear-gradient(0deg, rgba(0, 44, 79, 0.50) 0%, rgba(0, 44, 79, 0.50) 100%), url(${
            mediaPath(data.background.url)
          }) lightgray 50% / cover no-repeat`,
        } : undefined}
      >
        {cards && cards.map((card) => (
          <div
            key={card.id}
            className={clsx(styles.columnCompare, {
              [styles.badgeExist]: hasAnyBadge,
              [styles.withBadge]: !!card.badge,
            })}
          >
            {hasAnyBadge && <p className={styles.columnCompareBadge}>{card.badge}</p>}
            <div className={styles.columnCompareContent}>
              <div className={styles.columnCompareTop}>
                <Text
                  tag='h5'
                  className={styles.columnCompareTitle}
                >
                  {card.title}
                </Text>
                <div className={styles.columnComparePrice}>
                  <p className={styles.columnComparePriceValue}>{card.price}</p>
                  <p className={styles.columnComparePriceDuration}>{card.duration}</p>
                </div>
              </div>
              <div className={styles.columnCompareBottom}>
                <Text
                  tag='p'
                  className={styles.columnCompareDescription}
                >
                  {card.description}
                </Text>

                <Button
                  type={card.button.type}
                  className={styles.primaryButton}
                  iconName={card.button.iconName || 'arrow_forward'}
                  href={card.button.link}
                >
                  {card.button.title}
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default PlansSection;
